import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { adminAPI, competitionAPI, matchAPI, clubAPI, competitionTypeAPI } from '../../api/endpoints';
import { formatXPUNT, formatTimeAgo, formatDate } from '../../utils/helpers';
import { 
  FaUsers, FaTicketAlt, FaTrophy, FaHandshake,
  FaClock, FaCheck, FaTimes, FaEye,
  FaEdit, FaTrash, FaPlus, FaSearch,
  FaChartLine, FaCoins, FaBell, FaArrowLeft,
  FaShieldAlt, FaCrown, FaFootballBall,
  FaFlag, FaSave, FaUserFriends,
  FaChevronLeft, FaChevronRight
} from 'react-icons/fa';
import toast from 'react-hot-toast';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import './AdminDashboard.css';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [pendingValidations, setPendingValidations] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Data states
  const [teams, setTeams] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [competitions, setCompetitions] = useState([]);
  const [matches, setMatches] = useState([]);
  
  // Pagination states
  const [teamPagination, setTeamPagination] = useState({ currentPage: 1, itemsPerPage: 10, total: 0 });
  const [leaguePagination, setLeaguePagination] = useState({ currentPage: 1, itemsPerPage: 10, total: 0 });
  const [competitionPagination, setCompetitionPagination] = useState({ currentPage: 1, itemsPerPage: 10, total: 0 });
  const [matchPagination, setMatchPagination] = useState({ currentPage: 1, itemsPerPage: 10, total: 0 });
  
  // Search states
  const [teamSearch, setTeamSearch] = useState('');
  const [leagueSearch, setLeagueSearch] = useState('');
  const [competitionSearch, setCompetitionSearch] = useState('');
  const [matchSearch, setMatchSearch] = useState('');
  
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [matchResultData, setMatchResultData] = useState({});

  const isAdmin = user?.is_admin || 
                  user?.capabilities?.manage_options || 
                  user?.roles?.includes('administrator') ||
                  user?.roles?.includes('admin');

  // Fetch all data on mount
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    if (!isAdmin) {
      toast.error('Admin access required');
      navigate('/dashboard');
      return;
    }
    
    const fetchAllData = async () => {
      setLoading(true);
      try {
        await Promise.all([
          fetchAdminData(),
          fetchAllTeams(),
          fetchAllLeagues(),
          fetchAllCompetitions(),
          fetchAllMatches(),
        ]);
      } catch (error) {
        console.error('Failed to fetch admin data:', error);
        toast.error('Failed to load admin data');
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, [isAuthenticated, isAdmin, navigate]);

  // Fetch functions
  const fetchAdminData = async () => {
    try {
      const [statsRes, pendingRes, activityRes] = await Promise.all([
        adminAPI.getStats(),
        adminAPI.getPendingValidations(),
        adminAPI.getRecentActivity(),
      ]);
      setStats(statsRes.data || {});
      setPendingValidations(pendingRes.data || []);
      setRecentActivity(activityRes.data || []);
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
      throw error;
    }
  };

  const fetchAllTeams = async () => {
    try {
      const response = await clubAPI.getList({ per_page: 100 });
      setTeams(response.data.data || []);
      setTeamPagination(prev => ({ ...prev, total: response.data.total || 0 }));
    } catch (error) {
      console.error('Failed to fetch teams:', error);
      throw error;
    }
  };

  const fetchAllLeagues = async () => {
    try {
      const response = await competitionTypeAPI.getList({ per_page: 100 });
      setLeagues(response.data.data || []);
      setLeaguePagination(prev => ({ ...prev, total: response.data.total || 0 }));
    } catch (error) {
      console.error('Failed to fetch leagues:', error);
      throw error;
    }
  };

  const fetchAllCompetitions = async () => {
    try {
      const response = await competitionAPI.getList({ per_page: 100 });
      setCompetitions(response.data.data || []);
      setCompetitionPagination(prev => ({ ...prev, total: response.data.total || 0 }));
    } catch (error) {
      console.error('Failed to fetch competitions:', error);
      throw error;
    }
  };

  const fetchAllMatches = async () => {
    try {
      const response = await matchAPI.getList({ per_page: 100 });
      setMatches(response.data.data || []);
      setMatchPagination(prev => ({ ...prev, total: response.data.total || 0 }));
    } catch (error) {
      console.error('Failed to fetch matches:', error);
      throw error;
    }
  };

  // Pagination helper functions
  const paginateData = (data, currentPage, itemsPerPage) => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return data.slice(startIndex, endIndex);
  };

  const getTotalPages = (total, itemsPerPage) => {
    return Math.ceil(total / itemsPerPage);
  };

  // Filter functions
  const filterTeams = (data, search) => {
    if (!search) return data;
    const lowerSearch = search.toLowerCase();
    return data.filter(team => 
      team.name?.toLowerCase().includes(lowerSearch) ||
      team.country?.toLowerCase().includes(lowerSearch)
    );
  };

  const filterLeagues = (data, search) => {
    if (!search) return data;
    const lowerSearch = search.toLowerCase();
    return data.filter(league => 
      league.name?.toLowerCase().includes(lowerSearch) ||
      league.country?.toLowerCase().includes(lowerSearch)
    );
  };

  const filterCompetitions = (data, search) => {
    if (!search) return data;
    const lowerSearch = search.toLowerCase();
    return data.filter(comp => 
      comp.title?.toLowerCase().includes(lowerSearch) ||
      comp.description?.toLowerCase().includes(lowerSearch)
    );
  };

  const filterMatches = (data, search) => {
    if (!search) return data;
    const lowerSearch = search.toLowerCase();
    return data.filter(match => {
      const homeTeam = teams.find(t => t.id === match.home_club_id)?.name || match.home_team || '';
      const awayTeam = teams.find(t => t.id === match.away_club_id)?.name || match.away_team || '';
      const league = leagues.find(l => l.id === match.competition_id)?.name || match.league || '';
      return homeTeam.toLowerCase().includes(lowerSearch) || 
             awayTeam.toLowerCase().includes(lowerSearch) || 
             league.toLowerCase().includes(lowerSearch);
    });
  };

  // Get paginated and filtered data
  const getPaginatedData = (data, search, pagination, filterFn) => {
    const filtered = filterFn(data, search);
    const total = filtered.length;
    const paginated = paginateData(filtered, pagination.currentPage, pagination.itemsPerPage);
    return { data: paginated, total, totalPages: getTotalPages(total, pagination.itemsPerPage) };
  };

  // Modal handlers
  const handleModalOpen = (type, item = null) => {
    setModalType(type);
    setEditingItem(item);
    if (item) {
      setFormData(item);
    } else {
      setFormData({
        home_club_id: '',
        away_club_id: '',
        competition_id: '',
        venue: '',
        kickoff_time: '',
      });
    }
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditingItem(null);
    setFormData({});
    setMatchResultData({});
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleMatchResultChange = (e) => {
    const { name, value } = e.target;
    setMatchResultData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  // Validation handler
  const handleValidate = async (betId, status) => {
    try {
      await adminAPI.validateBet(betId, { status });
      toast.success(`Bet code ${status} successfully`);
      fetchAdminData();
    } catch (error) {
      toast.error('Failed to validate bet');
    }
  };

  // Team CRUD
  const handleSaveTeam = async () => {
    try {
      if (editingItem) {
        await clubAPI.update(editingItem.id, formData);
        toast.success('Team updated successfully');
      } else {
        await clubAPI.create(formData);
        toast.success('Team created successfully');
      }
      setShowModal(false);
      await fetchAllTeams();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save team');
    }
  };

  // League CRUD
  const handleSaveLeague = async () => {
    try {
      if (editingItem) {
        await competitionTypeAPI.update(editingItem.id, formData);
        toast.success('League updated successfully');
      } else {
        await competitionTypeAPI.create(formData);
        toast.success('League created successfully');
      }
      setShowModal(false);
      await fetchAllLeagues();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save league');
    }
  };

  // Competition CRUD
  const handleSaveCompetition = async () => {
    try {
      if (editingItem) {
        await competitionAPI.update(editingItem.id, formData);
        toast.success('Competition updated successfully');
      } else {
        await competitionAPI.create(formData);
        toast.success('Competition created successfully');
      }
      setShowModal(false);
      await fetchAllCompetitions();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save competition');
    }
  };

  // Match CRUD
  const handleSaveMatch = async () => {
    if (formData.home_club_id === formData.away_club_id) {
      toast.error('Home and Away teams must be different');
      return;
    }

    try {
      if (editingItem) {
        await matchAPI.update(editingItem.id, formData);
        toast.success('Match updated successfully');
      } else {
        await matchAPI.create(formData);
        toast.success('Match created successfully');
      }
      setShowModal(false);
      await fetchAllMatches();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save match');
    }
  };

  const handleUpdateMatchResult = async (matchId) => {
    if (matchResultData.home_score === undefined || matchResultData.away_score === undefined) {
      toast.error('Please enter both home and away scores');
      return;
    }
    if (!matchResultData.outcome) {
      toast.error('Please select an outcome');
      return;
    }

    try {
      await matchAPI.updateResult(matchId, matchResultData);
      toast.success('Match result updated successfully');
      setMatchResultData({});
      setShowModal(false);
      await fetchAllMatches();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update match result');
    }
  };

  const handleDeleteItem = async (type, id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    try {
      if (type === 'team') {
        await clubAPI.delete(id);
      } else if (type === 'league') {
        await competitionTypeAPI.delete(id);
      } else if (type === 'competition') {
        await competitionAPI.delete(id);
      } else if (type === 'match') {
        await matchAPI.delete(id);
      }
      toast.success('Deleted successfully');
      if (type === 'team') await fetchAllTeams();
      else if (type === 'league') await fetchAllLeagues();
      else if (type === 'competition') await fetchAllCompetitions();
      else if (type === 'match') await fetchAllMatches();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete');
    }
  };

  // Pagination change handlers
  const handlePageChange = (type, newPage) => {
    const setters = {
      teams: setTeamPagination,
      leagues: setLeaguePagination,
      competitions: setCompetitionPagination,
      matches: setMatchPagination
    };
    const setter = setters[type];
    if (setter) {
      setter(prev => ({ ...prev, currentPage: newPage }));
    }
  };

  // Pagination component
  const PaginationControls = ({ currentPage, totalPages, onPageChange }) => {
    if (totalPages <= 1) return null;
    
    const pages = [];
    const maxVisible = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);
    
    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return (
      <div className="pagination-controls">
        <button 
          className="page-btn"
          disabled={currentPage === 1}
          onClick={() => onPageChange(currentPage - 1)}
        >
          <FaChevronLeft />
        </button>
        {startPage > 1 && (
          <>
            <button className="page-btn" onClick={() => onPageChange(1)}>1</button>
            {startPage > 2 && <span className="page-ellipsis">...</span>}
          </>
        )}
        {pages.map(page => (
          <button 
            key={page}
            className={`page-btn ${page === currentPage ? 'active' : ''}`}
            onClick={() => onPageChange(page)}
          >
            {page}
          </button>
        ))}
        {endPage < totalPages && (
          <>
            {endPage < totalPages - 1 && <span className="page-ellipsis">...</span>}
            <button className="page-btn" onClick={() => onPageChange(totalPages)}>{totalPages}</button>
          </>
        )}
        <button 
          className="page-btn"
          disabled={currentPage === totalPages}
          onClick={() => onPageChange(currentPage + 1)}
        >
          <FaChevronRight />
        </button>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <div className="admin-header">
        <div className="admin-header-left">
          <button onClick={() => navigate(-1)} className="back-button">
            <FaArrowLeft /> Back
          </button>
          <div>
            <h1>
              <FaShieldAlt className="admin-icon" /> Admin Dashboard
            </h1>
            <p className="admin-subtitle">Manage your platform</p>
          </div>
        </div>
        <div className="admin-header-right">
          <span className="admin-badge">
            <FaCrown /> Admin
          </span>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="admin-stats">
        <div className="admin-stat-card">
          <FaUsers className="stat-icon users" />
          <div className="stat-info">
            <span className="stat-number">{stats.total_users || 0}</span>
            <span className="stat-label">Total Users</span>
          </div>
        </div>
        <div className="admin-stat-card">
          <FaTicketAlt className="stat-icon bets" />
          <div className="stat-info">
            <span className="stat-number">{stats.total_bet_codes || 0}</span>
            <span className="stat-label">Bet Codes</span>
          </div>
        </div>
        <div className="admin-stat-card">
          <FaTrophy className="stat-icon comps" />
          <div className="stat-info">
            <span className="stat-number">{stats.total_competitions || 0}</span>
            <span className="stat-label">Competitions</span>
          </div>
        </div>
        <div className="admin-stat-card">
          <FaHandshake className="stat-icon h2h" />
          <div className="stat-info">
            <span className="stat-number">{stats.total_h2h_games || 0}</span>
            <span className="stat-label">H2H Games</span>
          </div>
        </div>
        <div className="admin-stat-card">
          <FaClock className="stat-icon pending" />
          <div className="stat-info">
            <span className="stat-number">{stats.pending_validations || 0}</span>
            <span className="stat-label">Pending Validations</span>
          </div>
        </div>
        <div className="admin-stat-card">
          <FaCoins className="stat-icon fees" />
          <div className="stat-info">
            <span className="stat-number">{formatXPUNT(stats.system_fees || 0)}</span>
            <span className="stat-label">System Fees</span>
          </div>
        </div>
        <div className="admin-stat-card">
          <FaCoins className="stat-icon fees" />
          <div className="stat-info"> 
             <Link to="/admin/crypto/deposits" className="btn btn-primary">
              <span className="stat-label">Crypto Deposit</span>
            </Link>
           
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="admin-tabs">
        <button 
          className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          <FaChartLine /> Overview
        </button>
        <button 
          className={`tab-btn ${activeTab === 'validations' ? 'active' : ''}`}
          onClick={() => setActiveTab('validations')}
        >
          <FaCheck /> Validations
          {pendingValidations.length > 0 && (
            <span className="badge">{pendingValidations.length}</span>
          )}
        </button>
        <button 
          className={`tab-btn ${activeTab === 'teams' ? 'active' : ''}`}
          onClick={() => setActiveTab('teams')}
        >
          <FaUserFriends /> Teams
        </button>
        <button 
          className={`tab-btn ${activeTab === 'leagues' ? 'active' : ''}`}
          onClick={() => setActiveTab('leagues')}
        >
          <FaFlag /> Leagues
        </button>
        <button 
          className={`tab-btn ${activeTab === 'competitions' ? 'active' : ''}`}
          onClick={() => setActiveTab('competitions')}
        >
          <FaTrophy /> Competitions
        </button>
        <button 
          className={`tab-btn ${activeTab === 'matches' ? 'active' : ''}`}
          onClick={() => setActiveTab('matches')}
        >
          <FaFootballBall /> Matches
        </button>
        <button 
          className={`tab-btn ${activeTab === 'activity' ? 'active' : ''}`}
          onClick={() => setActiveTab('activity')}
        >
          <FaBell /> Activity
        </button>
      </div>

      {/* Tab Content */}
      <div className="admin-content">
        {/* Overview */}
        {activeTab === 'overview' && (
          <div className="overview-grid">
            <div className="admin-card">
              <h3>Quick Actions</h3>
              <div className="quick-actions">
                <button 
                  className="quick-action"
                  onClick={() => handleModalOpen('team')}
                >
                  <FaPlus /> Add Team
                </button>
                <button 
                  className="quick-action"
                  onClick={() => handleModalOpen('league')}
                >
                  <FaPlus /> Add League
                </button>
                <button 
                  className="quick-action"
                  onClick={() => handleModalOpen('competition')}
                >
                  <FaPlus /> Add Competition
                </button>
                <button 
                  className="quick-action"
                  onClick={() => handleModalOpen('match')}
                >
                  <FaPlus /> Add Match
                </button>
              </div>
            </div>

            <div className="admin-card">
              <h3>System Overview</h3>
              <div className="system-info">
                <div className="info-item">
                  <span>Total Transactions</span>
                  <span>{stats.total_transactions || 0}</span>
                </div>
                <div className="info-item">
                  <span>Total Prize Pot</span>
                  <span>{formatXPUNT(stats.total_pot || 0)}</span>
                </div>
                <div className="info-item">
                  <span>Pending Validations</span>
                  <span className="highlight">{stats.pending_validations || 0}</span>
                </div>
                <div className="info-item">
                  <span>Total Teams</span>
                  <span>{teams.length}</span>
                </div>
                <div className="info-item">
                  <span>Total Leagues</span>
                  <span>{leagues.length}</span>
                </div>
                <div className="info-item">
                  <span>Total Matches</span>
                  <span>{matches.length}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Validations */}
        {activeTab === 'validations' && (
          <div className="validations-section">
            <div className="admin-card">
              <h3>Pending Bet Validations</h3>
              {pendingValidations.length === 0 ? (
                <div className="empty-state">
                  <p>No pending validations 🎉</p>
                </div>
              ) : (
                <div className="validation-list">
                  {pendingValidations.map((bet) => (
                    <div key={bet.id} className="validation-item">
                      <div className="validation-info">
                        <div className="validation-header">
                          <span className="validation-code">{bet.code}</span>
                          <span className="validation-platform">{bet.platform}</span>
                          <span className={`validation-result ${bet.marked_result}`}>
                            {bet.marked_result === 'won' ? '✅ Won' : '❌ Lost'}
                          </span>
                        </div>
                        <div className="validation-meta">
                          <span>By: {bet.author}</span>
                          <span>{formatTimeAgo(bet.created_at)}</span>
                        </div>
                        {bet.proof_image && (
                          <div className="proof-preview">
                            <img 
                              src={bet.proof_image} 
                              alt="Proof" 
                              onClick={() => window.open(bet.proof_image, '_blank')}
                            />
                          </div>
                        )}
                      </div>
                      <div className="validation-actions">
                        <button 
                          className="btn btn-success btn-sm"
                          onClick={() => handleValidate(bet.id, 'approved')}
                        >
                          <FaCheck /> Approve
                        </button>
                        <button 
                          className="btn btn-danger btn-sm"
                          onClick={() => handleValidate(bet.id, 'rejected')}
                        >
                          <FaTimes /> Reject
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Teams */}
        {activeTab === 'teams' && (
          <div className="admin-card">
            <div className="card-header">
              <h3><FaUserFriends /> Teams</h3>
              <button 
                className="btn btn-primary btn-sm"
                onClick={() => handleModalOpen('team')}
              >
                <FaPlus /> Add Team
              </button>
            </div>
            <div className="search-wrapper">
              <FaSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search teams..."
                value={teamSearch}
                onChange={(e) => setTeamSearch(e.target.value)}
                className="search-input"
              />
            </div>
            {(() => {
              const { data: paginatedTeams, total, totalPages } = getPaginatedData(
                teams,
                teamSearch,
                teamPagination,
                filterTeams
              );
              return (
                <>
                  {paginatedTeams.length === 0 ? (
                    <div className="empty-state">
                      <p>No teams found</p>
                    </div>
                  ) : (
                    <>
                      <div className="table-container">
                        <table className="admin-table">
                          <thead>
                            <tr>
                              <th>Team Name</th>
                              <th>Country</th>
                              <th>Created</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {paginatedTeams.map((team) => (
                              <tr key={team.id}>
                                <td>
                                  <div className="team-cell">
                                    {team.logo && (
                                      <img src={team.logo} alt={team.name} className="team-logo-small" />
                                    )}
                                    <strong>{team.name}</strong>
                                  </div>
                                </td>
                                <td>{team.country || 'N/A'}</td>
                                <td>{formatDate(team.created_at)}</td>
                                <td>
                                  <button 
                                    className="action-btn edit"
                                    onClick={() => handleModalOpen('team', team)}
                                  >
                                    <FaEdit />
                                  </button>
                                  <button 
                                    className="action-btn delete"
                                    onClick={() => handleDeleteItem('team', team.id)}
                                  >
                                    <FaTrash />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <div className="table-footer">
                        <span className="table-info">
                          Showing {paginatedTeams.length} of {total} teams
                        </span>
                        <PaginationControls 
                          currentPage={teamPagination.currentPage}
                          totalPages={totalPages}
                          onPageChange={(page) => handlePageChange('teams', page)}
                        />
                      </div>
                    </>
                  )}
                </>
              );
            })()}
          </div>
        )}

        {/* Leagues */}
        {activeTab === 'leagues' && (
          <div className="admin-card">
            <div className="card-header">
              <h3><FaFlag /> Leagues</h3>
              <button 
                className="btn btn-primary btn-sm"
                onClick={() => handleModalOpen('league')}
              >
                <FaPlus /> Add League
              </button>
            </div>
            <div className="search-wrapper">
              <FaSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search leagues..."
                value={leagueSearch}
                onChange={(e) => setLeagueSearch(e.target.value)}
                className="search-input"
              />
            </div>
            {(() => {
              const { data: paginatedLeagues, total, totalPages } = getPaginatedData(
                leagues,
                leagueSearch,
                leaguePagination,
                filterLeagues
              );
              return (
                <>
                  {paginatedLeagues.length === 0 ? (
                    <div className="empty-state">
                      <p>No leagues found</p>
                    </div>
                  ) : (
                    <>
                      <div className="table-container">
                        <table className="admin-table">
                          <thead>
                            <tr>
                              <th>Name</th>
                              <th>Country</th>
                              <th>Created</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {paginatedLeagues.map((league) => (
                              <tr key={league.id}>
                                <td><strong>{league.name}</strong></td>
                                <td>{league.country || 'Global'}</td>
                                <td>{formatDate(league.created_at)}</td>
                                <td>
                                  <button 
                                    className="action-btn edit"
                                    onClick={() => handleModalOpen('league', league)}
                                  >
                                    <FaEdit />
                                  </button>
                                  <button 
                                    className="action-btn delete"
                                    onClick={() => handleDeleteItem('league', league.id)}
                                  >
                                    <FaTrash />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <div className="table-footer">
                        <span className="table-info">
                          Showing {paginatedLeagues.length} of {total} leagues
                        </span>
                        <PaginationControls 
                          currentPage={leaguePagination.currentPage}
                          totalPages={totalPages}
                          onPageChange={(page) => handlePageChange('leagues', page)}
                        />
                      </div>
                    </>
                  )}
                </>
              );
            })()}
          </div>
        )}

        {/* Competitions */}
        {activeTab === 'competitions' && (
          <div className="admin-card">
            <div className="card-header">
              <h3><FaTrophy /> Competitions</h3>
              <button 
                className="btn btn-primary btn-sm"
                onClick={() => handleModalOpen('competition')}
              >
                <FaPlus /> Add Competition
              </button>
            </div>
            <div className="search-wrapper">
              <FaSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search competitions..."
                value={competitionSearch}
                onChange={(e) => setCompetitionSearch(e.target.value)}
                className="search-input"
              />
            </div>
            {(() => {
              const { data: paginatedCompetitions, total, totalPages } = getPaginatedData(
                competitions,
                competitionSearch,
                competitionPagination,
                filterCompetitions
              );
              return (
                <>
                  {paginatedCompetitions.length === 0 ? (
                    <div className="empty-state">
                      <p>No competitions found</p>
                    </div>
                  ) : (
                    <>
                      <div className="table-container">
                        <table className="admin-table">
                          <thead>
                            <tr>
                              <th>Title</th>
                              <th>Status</th>
                              <th>Players</th>
                              <th>Prize Pool</th>
                              <th>Created</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {paginatedCompetitions.map((comp) => (
                              <tr key={comp.id}>
                                <td><strong>{comp.title}</strong></td>
                                <td>
                                  <span className={`status-badge ${comp.status}`}>
                                    {comp.status}
                                  </span>
                                </td>
                                <td>{comp.players_count || 0}</td>
                                <td>{formatXPUNT(comp.total_prize_pool || 0)}</td>
                                <td>{formatDate(comp.created_at)}</td>
                                <td>
                                  <button 
                                    className="action-btn edit"
                                    onClick={() => handleModalOpen('competition', comp)}
                                  >
                                    <FaEdit />
                                  </button>
                                  <button 
                                    className="action-btn delete"
                                    onClick={() => handleDeleteItem('competition', comp.id)}
                                  >
                                    <FaTrash />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <div className="table-footer">
                        <span className="table-info">
                          Showing {paginatedCompetitions.length} of {total} competitions
                        </span>
                        <PaginationControls 
                          currentPage={competitionPagination.currentPage}
                          totalPages={totalPages}
                          onPageChange={(page) => handlePageChange('competitions', page)}
                        />
                      </div>
                    </>
                  )}
                </>
              );
            })()}
          </div>
        )}

        {/* Matches */}
        {activeTab === 'matches' && (
          <div className="admin-card">
            <div className="card-header">
              <h3><FaFootballBall /> Matches</h3>
              <button 
                className="btn btn-primary btn-sm"
                onClick={() => handleModalOpen('match')}
              >
                <FaPlus /> Add Match
              </button>
            </div>
            <div className="search-wrapper">
              <FaSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search matches..."
                value={matchSearch}
                onChange={(e) => setMatchSearch(e.target.value)}
                className="search-input"
              />
            </div>
            {(() => {
              const { data: paginatedMatches, total, totalPages } = getPaginatedData(
                matches,
                matchSearch,
                matchPagination,
                filterMatches
              );
              return (
                <>
                  {paginatedMatches.length === 0 ? (
                    <div className="empty-state">
                      <p>No matches found</p>
                    </div>
                  ) : (
                    <>
                      <div className="table-container">
                        <table className="admin-table">
                          <thead>
                            <tr>
                              <th>Home</th>
                              <th>Away</th>
                              <th>League</th>
                              <th>Venue</th>
                              <th>Kickoff</th>
                              <th>Result</th>
                              <th>Status</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {paginatedMatches.map((match) => {
                              const homeTeam = teams.find(t => t.id === match.home_club_id)?.name || match.home_team || 'Unknown';
                              const awayTeam = teams.find(t => t.id === match.away_club_id)?.name || match.away_team || 'Unknown';
                              const league = leagues.find(l => l.id === match.competition_id)?.name || match.league || 'N/A';
                              
                              return (
                                <tr key={match.id}>
                                  <td><strong>{homeTeam}</strong></td>
                                  <td><strong>{awayTeam}</strong></td>
                                  <td>{league}</td>
                                  <td>{match.venue || 'N/A'}</td>
                                  <td>{formatDate(match.kickoff_time)}</td>
                                  <td>
                                    {match.is_ended ? (
                                      <span className="result-score">
                                        {match.home_score} - {match.away_score}
                                      </span>
                                    ) : (
                                      <span className="text-muted">Pending</span>
                                    )}
                                  </td>
                                  <td>
                                    <span className={`status-badge ${match.status}`}>
                                      {match.status}
                                    </span>
                                  </td>
                                  <td>
                                    <button 
                                    className="action-btn edit"
                                    onClick={() => handleModalOpen('match', match)}
                                  >
                                    <FaEdit />
                                  </button>
                                  {!match.is_ended && (
                                    <button 
                                      className="action-btn result"
                                      onClick={() => {
                                        setMatchResultData({
                                          home_score: '',
                                          away_score: '',
                                          outcome: '',
                                        });
                                        handleModalOpen('result', match);
                                      }}
                                    >
                                      <FaCheck />
                                    </button>
                                  )}
                                  <button 
                                    className="action-btn delete"
                                    onClick={() => handleDeleteItem('match', match.id)}
                                  >
                                    <FaTrash />
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    <div className="table-footer">
                      <span className="table-info">
                        Showing {paginatedMatches.length} of {total} matches
                      </span>
                      <PaginationControls 
                        currentPage={matchPagination.currentPage}
                        totalPages={totalPages}
                        onPageChange={(page) => handlePageChange('matches', page)}
                      />
                    </div>
                  </>
                )}
              </>
            );
          })()}
        </div>
      )}

      {/* Activity */}
      {activeTab === 'activity' && (
        <div className="activity-section">
          <div className="admin-card">
            <h3>Recent Activity</h3>
            {recentActivity.length === 0 ? (
              <div className="empty-state">
                <p>No recent activity</p>
              </div>
            ) : (
              <div className="activity-list">
                {recentActivity.map((item, index) => (
                  <div key={index} className="activity-item">
                    <div className="activity-icon" style={{ color: item.color }}>
                      <i className={item.icon}></i>
                    </div>
                    <div className="activity-content">
                      <p>{item.text}</p>
                      <span className="activity-time">{item.time}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>

    {/* Modal */}
    {showModal && (
      <div className="modal-overlay">
        <div className="modal-content">
          <div className="modal-header">
            <h2>
              {modalType === 'team' && (editingItem ? 'Edit Team' : 'Add Team')}
              {modalType === 'league' && (editingItem ? 'Edit League' : 'Add League')}
              {modalType === 'competition' && (editingItem ? 'Edit Competition' : 'Add Competition')}
              {modalType === 'match' && (editingItem ? 'Edit Match' : 'Add Match')}
              {modalType === 'result' && 'Update Match Result'}
            </h2>
            <button className="close-btn" onClick={handleModalClose}>
              <FaTimes />
            </button>
          </div>
          <div className="modal-body">
            {/* Team Form */}
            {modalType === 'team' && (
              <form>
                <div className="form-group">
                  <label>Team Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name || ''}
                    onChange={handleFormChange}
                    placeholder="e.g., Arsenal FC"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Country</label>
                  <input
                    type="text"
                    name="country"
                    value={formData.country || ''}
                    onChange={handleFormChange}
                    placeholder="e.g., England"
                  />
                </div>
                <div className="form-group">
                  <label>Logo URL</label>
                  <input
                    type="url"
                    name="logo"
                    value={formData.logo || ''}
                    onChange={handleFormChange}
                    placeholder="https://example.com/logo.png"
                  />
                </div>
              </form>
            )}

            {/* League Form */}
            {modalType === 'league' && (
              <form>
                <div className="form-group">
                  <label>League Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name || ''}
                    onChange={handleFormChange}
                    placeholder="e.g., Premier League"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Country</label>
                  <input
                    type="text"
                    name="country"
                    value={formData.country || ''}
                    onChange={handleFormChange}
                    placeholder="e.g., England"
                  />
                </div>
                <div className="form-group">
                  <label>Logo URL</label>
                  <input
                    type="url"
                    name="logo"
                    value={formData.logo || ''}
                    onChange={handleFormChange}
                    placeholder="https://example.com/logo.png"
                  />
                </div>
              </form>
            )}

            {/* Competition Form */}
            {modalType === 'competition' && (
              <form>
                <div className="form-group">
                  <label>Title *</label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title || ''}
                    onChange={handleFormChange}
                    placeholder="e.g., Premier League Predictions"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    name="description"
                    value={formData.description || ''}
                    onChange={handleFormChange}
                    placeholder="Describe the competition..."
                    rows={3}
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Start Date *</label>
                    <input
                      type="datetime-local"
                      name="start_date"
                      value={formData.start_date || ''}
                      onChange={handleFormChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>End Date *</label>
                    <input
                      type="datetime-local"
                      name="end_date"
                      value={formData.end_date || ''}
                      onChange={handleFormChange}
                      required
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Visibility</label>
                  <select
                    name="visibility"
                    value={formData.visibility || 'public'}
                    onChange={handleFormChange}
                  >
                    <option value="public">Public</option>
                    <option value="private">Private</option>
                  </select>
                </div>
                <div className="form-group checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="is_free"
                      checked={formData.is_free !== false}
                      onChange={handleFormChange}
                    />
                    Free Entry
                  </label>
                </div>
                {formData.is_free === false && (
                  <div className="form-group">
                    <label>Entry Price ($XPUNT)</label>
                    <input
                      type="number"
                      name="join_price"
                      value={formData.join_price || 0}
                      onChange={handleFormChange}
                      step="0.01"
                      min="0.01"
                    />
                  </div>
                )}
              </form>
            )}

            {/* Match Form */}
            {modalType === 'match' && (
              <form>
                <div className="form-group">
                  <label>Home Team *</label>
                  <select
                    name="home_club_id"
                    value={formData.home_club_id || ''}
                    onChange={handleFormChange}
                    required
                  >
                    <option value="">Select Home Team</option>
                    {teams.map((team) => (
                      <option key={team.id} value={team.id}>
                        {team.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Away Team *</label>
                  <select
                    name="away_club_id"
                    value={formData.away_club_id || ''}
                    onChange={handleFormChange}
                    required
                  >
                    <option value="">Select Away Team</option>
                    {teams.map((team) => (
                      <option key={team.id} value={team.id}>
                        {team.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>League *</label>
                  <select
                    name="competition_id"
                    value={formData.competition_id || ''}
                    onChange={handleFormChange}
                    required
                  >
                    <option value="">Select League</option>
                    {leagues.map((league) => (
                      <option key={league.id} value={league.id}>
                        {league.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Venue</label>
                  <input
                    type="text"
                    name="venue"
                    value={formData.venue || ''}
                    onChange={handleFormChange}
                    placeholder="e.g., Emirates Stadium"
                  />
                </div>
                <div className="form-group">
                  <label>Kickoff Time *</label>
                  <input
                    type="datetime-local"
                    name="kickoff_time"
                    value={formData.kickoff_time || ''}
                    onChange={handleFormChange}
                    required
                  />
                </div>
              </form>
            )}

            {/* Match Result Form */}
            {modalType === 'result' && (
              <form>
                <div className="match-result-info">
                  <h4>
                    {editingItem?.home_team || 
                      teams.find(t => t.id === editingItem?.home_club_id)?.name || 'Home'} 
                    vs 
                    {editingItem?.away_team || 
                      teams.find(t => t.id === editingItem?.away_club_id)?.name || 'Away'}
                  </h4>
                  <p className="match-league">
                    {editingItem?.league || 
                      leagues.find(l => l.id === editingItem?.competition_id)?.name || 'N/A'}
                  </p>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Home Score *</label>
                    <input
                      type="number"
                      name="home_score"
                      value={matchResultData.home_score || ''}
                      onChange={handleMatchResultChange}
                      min="0"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Away Score *</label>
                    <input
                      type="number"
                      name="away_score"
                      value={matchResultData.away_score || ''}
                      onChange={handleMatchResultChange}
                      min="0"
                      required
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Outcome *</label>
                  <select
                    name="outcome"
                    value={matchResultData.outcome || ''}
                    onChange={handleMatchResultChange}
                    required
                  >
                    <option value="">Select Outcome</option>
                    <option value="Home Win">Home Win</option>
                    <option value="Draw">Draw</option>
                    <option value="Away Win">Away Win</option>
                    <option value="Over 1.5">Over 1.5</option>
                    <option value="Under 1.5">Under 1.5</option>
                    <option value="Over 2.5">Over 2.5</option>
                    <option value="Under 2.5">Under 2.5</option>
                    <option value="BTTS">BTTS (Both Teams to Score)</option>
                    <option value="No BTTS">No BTTS</option>
                  </select>
                </div>
                <div className="form-actions">
                  <button 
                    type="button" 
                    className="btn btn-primary"
                    onClick={() => handleUpdateMatchResult(editingItem?.id)}
                  >
                    <FaSave /> Update Result
                  </button>
                </div>
              </form>
            )}
          </div>
          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={handleModalClose}>
              Cancel
            </button>
            {modalType !== 'result' && (
              <button 
                className="btn btn-primary"
                onClick={() => {
                  if (modalType === 'team') handleSaveTeam();
                  else if (modalType === 'league') handleSaveLeague();
                  else if (modalType === 'competition') handleSaveCompetition();
                  else if (modalType === 'match') handleSaveMatch();
                }}
              >
                <FaSave /> Save
              </button>
            )}
          </div>
        </div>
      </div>
    )}
  </div>
  );
};

export default AdminDashboard;